// Merge pack template settings.json vào project settings.json.
//
// Background:
//   - Pack ship templates/settings.json.tpl với `hooks`, `statusLine`, `permissions`
//     declare workflow team-shared (dev-rules-reminder, scout-block, ...).
//   - Avatar `init` ghi base settings.json với env Avatar vars + permissions baseline.
//   - Sau khi `sync` symlink farm xong, hooks pack KHÔNG active vì project
//     settings.json chưa khai báo hook entries → Claude Code không trigger.
//
// Strategy (Q1 confirmed: "Pack base + user override"):
//   - Pack template = baseline cho hooks + statusLine + permissions.
//   - User customization (env, model, existing permissions extras, existing hook entries
//     dùng matcher khác) → preserve.
//   - Deep merge field-level:
//       env             → user wins (giữ API key)
//       statusLine      → pack wins nếu user chưa set
//       permissions     → union (pack + user, dedupe)
//       hooks           → merge per-event, dedupe theo (matcher + command)
//       model           → user wins nếu set; pack default fallback
//       includeCoAuthoredBy → pack value áp dụng nếu user chưa set
//
// Safety:
//   - Backup .claude/settings.json → .claude/settings.json.backup-<timestamp> trước merge
//   - Atomic write file mới
//   - Return result để caller log từng action

import { promises as fs } from "node:fs";
import { join } from "node:path";
import { pathExists, readJson, readText, writeJsonAtomic } from "./filesystem-helpers.js";
import type { MergeSettingsResult, PackSettingsTemplate } from "./merge-settings-types.js";
import { applyScalarFieldMerges } from "./settings-field-mergers.js";
import { applyPackHooks } from "./settings-hooks-merge.js";
import { backupFilename } from "./settings-merge-primitives.js";
import { withSettingsFileLock } from "./with-settings-file-lock.js";
import { SECRET_FILE_MODE } from "./write-claude-settings-json-per-project.js";

// PATH_LIKE_TOKEN / checkPathToken / isCommandPathWithinWorkspace /
// isStatusLineCommandResolvable → tách sang settings-command-path-safety.ts (A6).
// Re-export isCommandPathWithinWorkspace giữ public API (apply-tool-manifest + test).
export { isCommandPathWithinWorkspace } from "./settings-command-path-safety.js";
export { unionDedupe } from "./settings-merge-primitives.js";
// PackSettingsTemplate + MergeSettingsResult → tách sang merge-settings-types.ts (A6).
// Re-export type giữ public API (apply-tool-manifest import type PackSettingsTemplate).
export type { MergeSettingsResult, PackSettingsTemplate };
// backupFilename + unionDedupe → tách sang settings-merge-primitives.ts (A6).
// backupFilename dùng nội bộ (backup step) → import + export; unionDedupe re-export thuần.
export { backupFilename };

// normalizeCommandForMigration / extractCommandsFromEntry / migrateStaleRelativePathHooks
// / mergeHooksPerEvent + step-6 → tách sang settings-hooks-merge.ts (A6, applyPackHooks).
// Các helper này INTERNAL bên hooks-merge — KHÔNG re-export (không nới public surface).

// Main entry. Đọc pack template + user settings, merge, backup + write atomic.
// Returns result để caller report.
export async function mergePackSettingsIntoProjectSettings(
  workspacePath: string,
): Promise<MergeSettingsResult> {
  const packTemplatePath = join(workspacePath, ".claude", "pack", "templates", "settings.json.tpl");
  const projectSettingsPath = join(workspacePath, ".claude", "settings.json");

  // Pack chưa sync hoặc không có template → no-op.
  if (!(await pathExists(packTemplatePath))) {
    return { action: "no-pack-template", changes: [] };
  }

  // #164 writer #1: read project settings + write PHẢI nằm trong CÙNG một bracket
  // lock. Bọc riêng lời gọi ghi (để read ngoài lock) = không sửa gì cả — tiến trình
  // khác vẫn chen được vào giữa và snapshot của ta thành lỗi thời.
  return await withSettingsFileLock(workspacePath, async () => {
    return await mergeUnderLock(packTemplatePath, projectSettingsPath, workspacePath);
  });
}

// Thân cũ của mergePackSettingsIntoProjectSettings, dịch nguyên văn vào đây để
// bracket lock ở trên đọc được thành 2 dòng thay vì thụt lề cả hàm 60 dòng.
// KHÔNG đổi một dòng logic nào.
async function mergeUnderLock(
  packTemplatePath: string,
  projectSettingsPath: string,
  workspacePath: string,
): Promise<MergeSettingsResult> {
  // Pack template có thể chứa placeholders {{...}}; chúng ta merge nguyên trạng
  // (placeholder sẽ giữ literal trong project settings nếu user chưa thay).
  // Tuy nhiên hooks + statusLine + permissions không dùng placeholder → safe.
  let packTemplate: PackSettingsTemplate;
  try {
    const raw = await readText(packTemplatePath);
    packTemplate = JSON.parse(raw) as PackSettingsTemplate;
  } catch (err) {
    throw new Error(
      `Pack settings template không parse được JSON: ${(err as Error).message}. Path: ${packTemplatePath}`,
    );
  }

  // Project settings — nếu chưa có (vd init bị skip) thì coi như empty base.
  let userSettings: PackSettingsTemplate = {};
  let projectHasSettings = false;
  if (await pathExists(projectSettingsPath)) {
    projectHasSettings = true;
    try {
      userSettings = await readJson<PackSettingsTemplate>(projectSettingsPath);
    } catch (err) {
      throw new Error(
        `Project settings.json không parse được: ${(err as Error).message}. Manual fix trước khi sync.`,
      );
    }
  }

  const changes: string[] = [];
  const merged: PackSettingsTemplate = { ...userSettings };

  // 0-5. scalar/permissions merges: disableWorkflows, statusLine, includeCoAuthoredBy,
  // model, env(+domain-rewrite), permissions. Lift sang settings-field-mergers.ts (A6).
  await applyScalarFieldMerges(merged, changes, packTemplate, userSettings, workspacePath);

  // 6. hooks — merge per-event + dedupe + migrate stale + security prescan.
  // Lift sang settings-hooks-merge.ts (A6): applyPackHooks mutate merged/changes.
  applyPackHooks(merged, changes, packTemplate, userSettings, workspacePath);

  // Không có thay đổi gì → skip write.
  if (changes.length === 0) {
    return { action: "no-change", changes: [] };
  }

  // Backup + write.
  let backupPath: string | undefined;
  if (projectHasSettings) {
    backupPath = backupFilename(projectSettingsPath);
    await fs.copyFile(projectSettingsPath, backupPath);
  }
  // #164 chốt (f): settings.json chứa ANTHROPIC_API_KEY. writeTextAtomic chmod tmp
  // rồi rename (THAY inode) ⇒ không truyền mode = mode đích theo umask (0644) ⇒ mỗi
  // `avatar sync` hạ file xuống world-readable.
  await writeJsonAtomic(projectSettingsPath, merged, SECRET_FILE_MODE);
  return { action: "merged", backupPath, changes };
}
